import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {MotivoDialog} from '@/components/motivo-dialog'
import {RegistrarConceptoDialog} from '@/components/concepto-registrar-dialog'
import {RestriccionesSection} from '@/components/restricciones-section'
import {RevisionConcepto} from '@/components/revision-concepto'
import {
  useAnularConcepto,
  useConceptos,
  type Concepto,
  type Restriccion,
} from '@/hooks/salud/medicina_laboral/use-conceptos'
import type {ConceptoAptitud} from '@/hooks/sgsst/evidencias/use-examenes'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'

/** Una sola función de color para las dos listas; `aplazado` y `sin_concepto` van en curso (D-M). */
export function veredictoTone(
  concepto: ConceptoAptitud,
  {anulado = false, normaAnterior = false}: {anulado?: boolean; normaAnterior?: boolean} = {}
): 'green' | 'amber' | 'red' | 'neutral' {
  if (anulado) return 'neutral'
  if (concepto === 'apto') return 'green'
  if (concepto === 'apto_con_restricciones') return 'amber'
  if (concepto === 'aplazado' || concepto === 'sin_concepto') return 'neutral'
  // Un «no apto» de la norma anterior no va rojo por viejo: su color sale de su vencimiento.
  return normaAnterior ? 'neutral' : 'red'
}

/** `reemplazado_por_concepto_posterior` es una clave de copy; los demás motivos los escribió una persona. */
export function motivoAnulado(motivo: string | null | undefined, t: (k: string) => string): string {
  if (!motivo) return ''
  return motivo === 'reemplazado_por_concepto_posterior' ? t(`concepto.motivo.${motivo}`) : motivo
}

/** D-N: si el vencimiento del examen y el del perfil difieren, se imprimen los dos y Kairos no escoge. */
function Vencimientos({concepto}: {concepto: Concepto}) {
  const {t} = useTranslation()
  if (concepto.sin_periodicidad && !concepto.vence_el_perfil) {
    // Sin la cifra de la empresa un silencio se leería como «no vence» (D-N).
    return (
      <p className="mt-1 text-xs text-muted-foreground" data-testid={`concepto-sin-periodicidad-${concepto.id}`}>
        {t('concepto.sinPeriodicidadVerificada')}
      </p>
    )
  }
  if (!concepto.vence_el && !concepto.vence_el_perfil) return null
  const difieren = !!concepto.vence_el && !!concepto.vence_el_perfil && concepto.vence_el !== concepto.vence_el_perfil
  return (
    <div className="mt-1 space-y-0.5" data-testid={`concepto-vencimientos-${concepto.id}`}>
      {concepto.vence_el && (
        <p className="font-mono text-[11px] text-muted-foreground">
          {t('concepto.venceEl', {fecha: formatDateOnly(concepto.vence_el)})}
          {concepto.vigencia_base && ` · ${t(`concepto.base.${concepto.vigencia_base}`)}`}
        </p>
      )}
      {concepto.vence_el_perfil && (
        <p className="font-mono text-[11px] text-muted-foreground">
          {t('concepto.venceElPerfil', {fecha: formatDateOnly(concepto.vence_el_perfil)})}
        </p>
      )}
      {difieren && <p className="text-xs text-muted-foreground">{t('concepto.dosFechas')}</p>}
    </div>
  )
}

function Restricciones({concepto, reducido}: {concepto: Concepto; reducido: boolean}) {
  const {t} = useTranslation()
  if (concepto.restricciones_total === 0) {
    return concepto.concepto_aptitud === 'apto_con_restricciones' ? (
      <p className="mt-1 text-xs text-muted-foreground">{t('examenes.sinRestricciones')}</p>
    ) : null
  }
  if (reducido) {
    return (
      <p className="mt-1 text-xs text-muted-foreground" data-testid={`concepto-restricciones-reducidas-${concepto.id}`}>
        {t('concepto.reducidoModoDios', {total: concepto.restricciones_total})}
      </p>
    )
  }
  return (
    <ul className="mt-1 space-y-0.5" data-testid={`concepto-restricciones-${concepto.id}`}>
      {concepto.restricciones.map((restriccion: Restriccion) => (
        <li key={restriccion.id} className="text-xs text-foreground">
          {restriccion.texto}
          <span className="ml-1.5 text-muted-foreground">
            {restriccion.temporalidad === 'temporal' && restriccion.fecha_fin
              ? t('concepto.hastaEl', {fecha: formatDateOnly(restriccion.fecha_fin)})
              : t(`concepto.temporalidad.${restriccion.temporalidad}`)}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** Anular no es borrar (§4): la fila queda marcada y el motivo escrito es obligatorio. */
function AnularConceptoDialog({
  businessId,
  employeeId,
  concepto,
}: {
  businessId: string
  employeeId: string
  concepto: Concepto
}) {
  const {t} = useTranslation()
  const anular = useAnularConcepto(businessId, employeeId)
  return (
    <MotivoDialog
      triggerLabel={t('concepto.anular.action')}
      triggerTestId={`concepto-anular-${concepto.id}`}
      title={t('concepto.anular.title')}
      description={t('concepto.anular.description')}
      motivoLabel={t('concepto.anular.motivoLabel')}
      motivoTestId="concepto-anular-motivo"
      submitLabel={t('concepto.anular.action')}
      onSubmit={(motivo) => anular.mutateAsync({examenId: concepto.id, motivo})}
    />
  )
}

/** E1-H05. Registrar es un acto de custodia: el dueño lo ve y no lo escribe. */
export function EmployeeConceptosSection({
  businessId,
  employeeId,
  canOperate,
  canRegistrar,
  registroBloqueadoPor,
}: {
  businessId: string
  employeeId: string | null
  canOperate: boolean
  // Registrar es del custodio de salud (§4); sin permiso se apaga con motivo, nunca se esconde.
  canRegistrar: boolean
  registroBloqueadoPor: string | null
}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  // Decidir una restricción es la misma custodia (§4); confirmar la acción del plan es firma.
  const capabilities = useCapabilities(businessId)
  const {data, isLoading, isError, error} = useConceptos(businessId, employeeId)
  if (!canOperate) return null
  if (isLoading) return <Skeleton className="h-20 w-full" />
  const puedeRegistrar = canRegistrar && !godReadOnly && !!employeeId
  const reducido = data?.detalle_reducido ?? false
  return (
    <div className="space-y-3 border-t border-border pt-4" data-testid="empleado-conceptos">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-foreground">{t('concepto.titulo')}</h3>
        {puedeRegistrar && employeeId ? (
          <RegistrarConceptoDialog businessId={businessId} employeeId={employeeId} />
        ) : (
          registroBloqueadoPor && (
            <p className="text-xs text-muted-foreground" data-testid="concepto-registro-bloqueado">
              {registroBloqueadoPor}
            </p>
          )
        )}
      </div>
      {isError ? (
        /* En salud, pintar el vacío ante un 403 afirmaría que no hay restricciones. */
        <p className="text-sm text-destructive" data-testid="empleado-conceptos-error">
          {translateApiError(error)}
        </p>
      ) : !data || data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="empleado-conceptos-empty">
          {t('concepto.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-border" data-testid="empleado-conceptos-list">
          {data.items.map((concepto) => (
            <li key={concepto.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {t(`empleadoExamenes.tipo.${concepto.tipo}`)}
                  <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                    {formatDateOnly(concepto.fecha)}
                  </span>
                </p>
                {!concepto.apaga_la_casilla && !concepto.anulado_en && (
                  <p className="text-xs text-muted-foreground" data-testid={`concepto-en-curso-${concepto.id}`}>
                    {t('examenes.soporteRecibido')}
                  </p>
                )}
                {concepto.fecha_futura && (
                  <p className="text-xs text-muted-foreground" data-testid={`concepto-fecha-futura-${concepto.id}`}>
                    {t('concepto.fechaFutura')}
                  </p>
                )}
                {concepto.aplazado_motivo && (
                  <p className="text-xs text-muted-foreground">
                    {concepto.aplazado_motivo}
                    {concepto.fecha_control &&
                      ` · ${t('concepto.control', {fecha: formatDateOnly(concepto.fecha_control)})}`}
                  </p>
                )}
                <Restricciones concepto={concepto} reducido={reducido} />
                <Vencimientos concepto={concepto} />
                {concepto.cargo_leido_texto && (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid={`concepto-cargo-leido-${concepto.id}`}>
                    {t('concepto.cargoLeido', {cargo: concepto.cargo_leido_texto})}
                  </p>
                )}
                {concepto.medico_nombre && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t('concepto.medico', {nombre: concepto.medico_nombre})}
                  </p>
                )}
                {concepto.anulado_en && (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid={`concepto-anulado-${concepto.id}`}>
                    {t('concepto.anulado', {motivo: motivoAnulado(concepto.anulado_motivo, t)})}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Tag
                  tone={veredictoTone(concepto.concepto_aptitud, {anulado: !!concepto.anulado_en})}
                  data-testid={`concepto-veredicto-${concepto.id}`}>
                  {t(`concepto.veredicto.${concepto.concepto_aptitud}`)}
                </Tag>
                {puedeRegistrar && employeeId && !concepto.anulado_en && (
                  <AnularConceptoDialog businessId={businessId} employeeId={employeeId} concepto={concepto} />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {employeeId && (
        <RestriccionesSection
          businessId={businessId}
          employeeId={employeeId}
          canOperate={canOperate}
          canDecidir={canRegistrar}
          canFirmar={capabilities.sgsst_firmar}
        />
      )}
      {employeeId && <RevisionConcepto businessId={businessId} employeeId={employeeId} />}
    </div>
  )
}
